class GvsHtmlSitemapSearch {
  constructor(root) {
    this.root = root;
    this.section = root.closest('[data-gvs-html-sitemap]');
    this.input = root.querySelector('[data-gvs-sitemap-search-input]');
    this.clearButton = root.querySelector('[data-gvs-sitemap-search-clear]');
    this.results = root.querySelector('[data-gvs-sitemap-search-results]');
    this.status = root.querySelector('[data-gvs-sitemap-search-status]');
    this.endpoint = root.dataset.endpoint;
    this.searchUrl = root.dataset.searchUrl;
    this.minChars = Number.parseInt(root.dataset.minChars, 10) || 2;
    this.abortController = null;
    this.timer = null;

    if (!this.input || !this.results || !this.endpoint || !this.section) return;

    this.labels = {
      local: root.dataset.localLabel || 'Site directory',
      products: root.dataset.productLabel || 'Products',
      collections: root.dataset.collectionLabel || 'Collections',
      pages: root.dataset.pageLabel || 'Pages & Support',
      articles: root.dataset.articleLabel || 'Guides & Advice'
    };

    this.localIndex = this.collectLocalIndex();
    this.input.addEventListener('input', () => this.handleInput());
    this.input.addEventListener('keydown', (event) => this.handleKeydown(event));
    this.results.addEventListener('keydown', (event) => this.handleResultsKeydown(event));
    this.clearButton?.addEventListener('click', () => this.clear());
    document.addEventListener('click', (event) => {
      if (!this.root.contains(event.target)) this.closeResults();
    });
  }

  collectLocalIndex() {
    const seen = new Set();
    const items = [];

    this.section.querySelectorAll('[data-gvs-sitemap-index-link]').forEach((link) => {
      const href = link.getAttribute('href');
      const title = link.dataset.gvsSitemapIndexTitle || link.textContent.trim();
      if (!href || !title) return;

      const key = this.normaliseUrl(href);
      if (!key || seen.has(key)) return;
      seen.add(key);

      items.push({
        title,
        url: href,
        type: this.normaliseType(link.dataset.gvsSitemapIndexType),
        context: link.dataset.gvsSitemapIndexContext || ''
      });
    });

    return items;
  }

  normaliseType(value) {
    const type = String(value || '').toLowerCase();
    if (type.includes('page')) return 'pages';
    if (type.includes('collection')) return 'collections';
    if (type.includes('article') || type.includes('blog')) return 'articles';
    if (type.includes('product')) return 'products';
    return 'local';
  }

  normalise(value) {
    return String(value || '')
      .toLowerCase()
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
  }

  normaliseUrl(value) {
    try {
      const url = new URL(value, window.location.origin);
      return `${url.pathname.replace(/\/$/, '') || '/'}${url.search}`.toLowerCase();
    } catch {
      return String(value || '').toLowerCase();
    }
  }

  scoreLocal(item, term) {
    const query = this.normalise(term);
    const title = this.normalise(item.title);
    const context = this.normalise(item.context);
    const tokens = query.split(' ').filter(Boolean);

    if (!query || !tokens.length) return 0;
    if (title === query) return 140;
    if (title.startsWith(query)) return 118;
    if (title.includes(query)) return 96;
    if (tokens.every((token) => title.includes(token))) return 84;
    if (context === query) return 72;
    if (context.includes(query)) return 58;
    if (tokens.every((token) => `${title} ${context}`.includes(token))) return 44;
    return 0;
  }

  searchLocal(term) {
    return this.localIndex
      .map((item) => ({ ...item, score: this.scoreLocal(item, term) }))
      .filter((item) => item.score > 0)
      .sort((a, b) => b.score - a.score || a.title.localeCompare(b.title))
      .slice(0, 14);
  }

  titleMatchesTerm(title, term) {
    const normalisedTitle = this.normalise(title);
    const tokens = this.normalise(term).split(' ').filter(Boolean);
    return tokens.length > 0 && tokens.every((token) => normalisedTitle.includes(token));
  }

  handleInput() {
    window.clearTimeout(this.timer);
    const term = this.input.value.trim();
    if (this.clearButton) this.clearButton.hidden = term.length === 0;

    if (term.length < this.minChars) {
      this.abortController?.abort();
      this.closeResults();
      this.updateStatus('');
      return;
    }

    this.timer = window.setTimeout(() => this.search(term), 140);
  }

  handleKeydown(event) {
    if (event.key === 'Escape') {
      this.closeResults();
      this.input.focus();
      return;
    }

    if (event.key !== 'ArrowDown' || this.results.hidden) return;
    const firstLink = this.results.querySelector('a');
    if (firstLink) {
      event.preventDefault();
      firstLink.focus();
    }
  }

  handleResultsKeydown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.closeResults();
      this.input.focus();
      return;
    }

    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;

    const links = Array.from(this.results.querySelectorAll('a'));
    if (!links.length) return;

    const currentIndex = links.indexOf(document.activeElement);
    event.preventDefault();

    if (event.key === 'ArrowUp' && currentIndex <= 0) {
      this.input.focus();
      return;
    }

    const direction = event.key === 'ArrowDown' ? 1 : -1;
    const nextIndex = currentIndex < 0
      ? (direction > 0 ? 0 : links.length - 1)
      : Math.min(Math.max(currentIndex + direction, 0), links.length - 1);

    links[nextIndex].focus();
  }

  async search(term) {
    this.abortController?.abort();
    this.abortController = new AbortController();
    this.updateStatus(this.root.dataset.loadingText || 'Searching GVS…');

    const local = this.searchLocal(term);
    const params = new URLSearchParams({
      q: term,
      'resources[type]': 'product,collection,page,article',
      'resources[limit]': '5',
      'resources[limit_scope]': 'each',
      'resources[options][unavailable_products]': 'last'
    });

    try {
      const response = await fetch(`${this.endpoint}?${params.toString()}`, {
        headers: { Accept: 'application/json' },
        signal: this.abortController.signal
      });

      if (!response.ok) throw new Error(`Predictive search returned ${response.status}`);

      const data = await response.json();
      const remote = data?.resources?.results || {};
      this.render(local, remote, term);
    } catch (error) {
      if (error.name === 'AbortError') return;
      this.render(local, {}, term);
    }
  }

  dedupeRemote(items, seen, term) {
    return (Array.isArray(items) ? items : []).filter((item) => {
      if (!item?.url || !item?.title || !this.titleMatchesTerm(item.title, term)) return false;
      const key = this.normaliseUrl(item.url);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  render(local, remote, term) {
    this.results.scrollTop = 0;
    this.results.replaceChildren();
    const seen = new Set();
    const buckets = {
      pages: [],
      collections: [],
      articles: [],
      local: [],
      products: []
    };

    local.forEach((item) => {
      const key = this.normaliseUrl(item.url);
      if (seen.has(key)) return;
      seen.add(key);
      const bucket = buckets[item.type] ? item.type : 'local';
      buckets[bucket].push(item);
    });

    ['pages', 'collections', 'articles', 'products'].forEach((type) => {
      const items = this.dedupeRemote(remote[type], seen, term);
      buckets[type].push(...items);
    });

    /*
     * Quick Find is product-first for commercial intent:
     * products are prioritised first, followed by broader collection routes,
     * then pages/support, guides and any residual local directory matches.
     * Six visible suggestions keeps the panel useful without turning it into
     * another scroll-heavy directory.
     */
    const maxTotal = 6;
    let total = 0;
    const renderQueue = [];
    const resultOrder = ['products', 'collections', 'pages', 'articles', 'local'];
    const resultLimits = {
      collections: 2,
      products: 3,
      pages: 1,
      articles: 1,
      local: 1
    };

    resultOrder.forEach((type) => {
      if (total >= maxTotal || !buckets[type].length) return;
      const remaining = maxTotal - total;
      const items = buckets[type].slice(0, Math.min(resultLimits[type], remaining));
      if (!items.length) return;
      renderQueue.push({ type, items });
      total += items.length;
    });

    renderQueue.forEach(({ type, items }) => {
      const group = document.createElement('section');
      group.className = `gvs-sitemap-search__group gvs-sitemap-search__group--${type}`;

      const label = document.createElement('p');
      label.className = 'gvs-sitemap-search__group-label';
      label.textContent = `${this.labels[type]} (${items.length})`;
      group.appendChild(label);

      const list = document.createElement('ul');
      list.className = 'gvs-sitemap-search__list';
      list.setAttribute('role', 'list');

      items.forEach((item) => {
        const li = document.createElement('li');
        li.className = 'gvs-sitemap-search__result-item';
        const link = document.createElement('a');
        link.className = 'gvs-sitemap-search__result-link';
        link.dataset.resultType = type;
        link.href = item.url;

        const copy = document.createElement('span');
        copy.className = 'gvs-sitemap-search__result-copy';

        const title = document.createElement('span');
        title.className = 'gvs-sitemap-search__result-title';
        title.textContent = item.title;
        copy.appendChild(title);

        if (item.context && this.normalise(item.context) !== this.normalise(item.title)) {
          const context = document.createElement('span');
          context.className = 'gvs-sitemap-search__result-context';
          context.textContent = item.context;
          copy.appendChild(context);
        }

        link.appendChild(copy);
        li.appendChild(link);
        list.appendChild(li);
      });

      group.appendChild(list);
      this.results.appendChild(group);
    });

    if (!total) {
      const empty = document.createElement('p');
      empty.className = 'gvs-sitemap-search__empty';
      empty.textContent = this.root.dataset.noResults || 'No matches found.';
      this.results.appendChild(empty);
    }

    if (this.searchUrl && term) {
      const footer = document.createElement('div');
      footer.className = 'gvs-sitemap-search__footer';
      const viewAll = document.createElement('a');
      viewAll.className = 'gvs-sitemap-search__view-all';
      viewAll.href = `${this.searchUrl}?q=${encodeURIComponent(term)}`;
      viewAll.textContent = this.root.dataset.viewAllLabel || 'View all search results';
      footer.appendChild(viewAll);
      this.results.appendChild(footer);
    }

    this.openResults();
    const suffix = this.root.dataset.suggestionsStatus || 'suggestions available';
    this.updateStatus(total ? `${total} ${suffix}.` : this.root.dataset.noResults || 'No matches found.');
  }

  openResults() {
    this.results.scrollTop = 0;
    this.results.hidden = false;
    this.input.setAttribute('aria-expanded', 'true');
  }

  closeResults() {
    this.results.hidden = true;
    this.results.scrollTop = 0;
    this.input.setAttribute('aria-expanded', 'false');
  }

  clear() {
    this.abortController?.abort();
    this.input.value = '';
    if (this.clearButton) this.clearButton.hidden = true;
    this.closeResults();
    this.updateStatus('');
    this.input.focus();
  }

  updateStatus(message) {
    if (this.status) this.status.textContent = message;
  }
}


function initialiseDirectoryDisclosure(section) {
  const allGroups = Array.from(section.querySelectorAll('.gvs-sitemap-menu__group'));
  const mobileQuery = window.matchMedia('(max-width: 749px)');
  const useGlobalMobileAccordion = section.dataset.collapseMobile === 'true';

  allGroups.forEach((group) => {
    group.addEventListener('toggle', () => {
      if (!group.open) return;

      if (mobileQuery.matches && useGlobalMobileAccordion) {
        allGroups.forEach((other) => {
          if (other !== group && other.open) other.removeAttribute('open');
        });
        return;
      }

      const menu = group.closest('.gvs-sitemap-menu');
      if (!menu) return;

      Array.from(menu.children)
        .filter((child) => child.matches?.('.gvs-sitemap-menu__group'))
        .forEach((other) => {
          if (other !== group && other.open) other.removeAttribute('open');
        });
    });
  });
}

function initialiseGvsHtmlSitemap(scope = document) {
  scope.querySelectorAll('[data-gvs-html-sitemap]').forEach((section) => {
    if (section.dataset.gvsSitemapReady === 'true') return;
    section.dataset.gvsSitemapReady = 'true';

    if (section.dataset.collapseMobile === 'true' && window.matchMedia('(max-width: 749px)').matches) {
      section.querySelectorAll('.gvs-sitemap-menu__group[open]').forEach((details) => details.removeAttribute('open'));
    }

    initialiseDirectoryDisclosure(section);
    section.querySelectorAll('[data-gvs-sitemap-search]').forEach((search) => new GvsHtmlSitemapSearch(search));
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initialiseGvsHtmlSitemap(), { once: true });
} else {
  initialiseGvsHtmlSitemap();
}

document.addEventListener('shopify:section:load', (event) => initialiseGvsHtmlSitemap(event.target));
